import { Spin } from "antd";
import { Tree } from "@lhd/types";
import { FC, useCallback, useEffect, useState } from "react";
import { BiChevronRight } from "react-icons/bi";
import styled from "styled-components";
import { ModelNameType } from "..";
import axiosInstance from "@lhd/libs/axios";
import { getCompanyId } from "@lhd/utility";
import { SERVICES } from "@lhd/constants";

const Item = styled.div`
  padding-left: 1rem;
  .inner {
    padding: 0.5rem;
    display: flex;
    align-items: center;
    justify-content: space-between;
    cursor: pointer;
    color: #666;
    &.selected,
    &:hover {
      color: #fff;
      background-color: #71a5e4;
    }
    svg {
      &.expanded {
        transform: rotate(90deg);
      }
    }
  }
`;

type BranchItemPropsType = {
  branch: Tree;
  selectedBranch?: Tree;
  onSelect: (branch: Tree) => void;
};

const BranchItem: FC<BranchItemPropsType> = ({
  branch,
  selectedBranch,
  onSelect,
}) => {
  const [children, setChildren] = useState<Tree[]>([]);
  const [expand, setExpand] = useState(false);
  const isSelected = selectedBranch?.id === branch.id;
  const onSelectBranch = useCallback(
    (e) => {
      if (branch?.children?.length > 0) {
        !isSelected && setChildren(branch.children);
      }
      onSelect(branch);
    },
    [branch, isSelected, onSelect],
  );

  useEffect(() => {
    if (selectedBranch === branch) {
      setExpand(true);
    }
  }, [selectedBranch, branch]);

  const onToggleExpand = useCallback(
    (e) => {
      e.stopPropagation();
      if (expand) {
        setChildren([]);
      } else {
        setChildren(branch.children);
      }
      setExpand(!expand);
    },
    [expand, branch],
  );
  return (
    <Item>
      <div
        onClick={onSelectBranch}
        className={isSelected ? "inner selected" : "inner"}
      >
        {branch.name || branch?.person?.name}
        {branch?.children?.length > 0 && (
          <BiChevronRight
            className={expand ? "expanded" : "closed"}
            onClick={onToggleExpand}
            color="#222"
            size={20}
          />
        )}
      </div>
      {children.map((child) => (
        <BranchItem
          key={child.id}
          branch={child}
          onSelect={onSelect}
          selectedBranch={selectedBranch}
        />
      ))}
    </Item>
  );
};

type BranchListPropsType = {
  modelName: ModelNameType;
  selectedBranch?: Tree;
  setSelectedBranch: (branch: Tree) => void;
};

const BranchList: FC<BranchListPropsType> = ({
  selectedBranch,
  setSelectedBranch,
  modelName,
}) => {
  const [loading, setLoading] = useState(true);
  const [trees, setTrees] = useState<Tree[]>([]);
  const isWorkerSelection = modelName === "worker";
  const fetchOrganizations = useCallback(() => {
    setLoading(true);
    axiosInstance({
      method: "get",
      url: isWorkerSelection
        ? `${SERVICES.WORKERS}/tree`
        : `${SERVICES.COMPANIES}/${getCompanyId()}/tree`,
      params: isWorkerSelection
        ? {
            page: 1,
            per_page: 1000000,
            offset: 0,
          }
        : {
            model_name: modelName,
          },
    })
      .then((res) => {
        if (isWorkerSelection) {
          const { branchs, departments, divisions, organizations } = res.data;
          const tree = [
            ...branchs,
            ...departments,
            ...divisions,
            ...organizations,
          ]
            .filter((item) => item.workers.length)
            .map((item) => {
              return {
                ...item,
                children: item.workers,
              };
            });
          setTrees((tree as Tree[]) || []);
        } else {
          setTrees((res.data as Tree[]) || []);
        }
      })
      .finally(() => setLoading(false));
  }, [modelName, isWorkerSelection]);

  useEffect(() => fetchOrganizations(), [fetchOrganizations]);
  return (
    <div className="branch-list">
      <Spin spinning={loading}>
        {trees?.map((branch) => (
          <BranchItem
            key={branch.id}
            branch={branch}
            onSelect={setSelectedBranch}
            selectedBranch={selectedBranch}
          />
        ))}
      </Spin>
    </div>
  );
};

export default BranchList;
